import React, { useState, useMemo, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { getAllTrainings, industryTrainings } from '../../data/industries/industryTrainingsData';
import { TrainingCard } from './TrainingCard';
import { TrainingFilters } from './TrainingFilters';
import { BookOpen, Factory, Award, Users, Layers } from 'lucide-react';

// Stats Card Component
const StatCard = ({ icon: Icon, value, label, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className={`bg-gradient-to-br ${color} rounded-xl p-6 text-center`}
  >
    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-3">
      <Icon className="h-6 w-6 text-white" />
    </div>
    <div className="text-3xl font-bold text-white mb-1">{value}</div>
    <div className="text-sm text-white/80">{label}</div>
  </motion.div>
);

export const AllTrainingsView = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');

  // Get all trainings and sort alphabetically
  const allTrainings = useMemo(() => {
    return getAllTrainings().sort((a, b) => a.title.localeCompare(b.title));
  }, []);

  // Calculate stats
  const stats = useMemo(() => {
    const certifications = new Set();
    allTrainings.forEach(t => {
      if (t.certificationLevels) {
        t.certificationLevels.forEach(c => certifications.add(c));
      }
      if (t.certification) {
        certifications.add(t.certification);
      }
    });

    const deliveryModes = new Set();
    allTrainings.forEach(t => {
      if (t.deliveryMode) {
        t.deliveryMode.forEach(m => deliveryModes.add(m));
      }
    });

    return {
      totalTrainings: allTrainings.length,
      totalIndustries: industryTrainings.length,
      totalCertifications: certifications.size,
      deliveryModes: deliveryModes.size,
    };
  }, [allTrainings]);

  // Filter trainings based on criteria
  const filteredTrainings = useMemo(() => {
    return allTrainings.filter(training => {
      // Industry filter
      const matchesIndustry = !industryFilter || industryFilter === 'all' ||
        training.industry.slug === industryFilter;

      // Level filter
      const matchesLevel = !levelFilter || levelFilter === 'all' ||
        training.level.toLowerCase().includes(levelFilter.toLowerCase());

      // Mode filter
      const matchesMode = !modeFilter || modeFilter === 'all' ||
        training.deliveryMode?.some(mode =>
          mode.toLowerCase() === modeFilter.toLowerCase()
        );

      // Search filter
      const matchesSearch = !searchQuery ||
        training.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        training.shortDescription.toLowerCase().includes(searchQuery.toLowerCase()) ||
        training.industry.name.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesIndustry && matchesLevel && matchesMode && matchesSearch;
    });
  }, [allTrainings, industryFilter, levelFilter, modeFilter, searchQuery]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <div className="py-12">
      {/* Stats Section */}
      <section className="container mx-auto px-4 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Explore Our <span className="text-blue-600">Complete Training Catalog</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Browse all our training programs across industries. Filter by industry, level, or delivery mode to find the perfect program for you.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <StatCard
            icon={BookOpen}
            value={`${stats.totalTrainings}+`}
            label="Training Programs"
            color="from-blue-500 to-blue-600"
          />
          <StatCard
            icon={Factory}
            value={stats.totalIndustries}
            label="Industries Covered"
            color="from-purple-500 to-purple-600"
          />
          <StatCard
            icon={Award}
            value={`${stats.totalCertifications}+`}
            label="Certifications"
            color="from-green-500 to-green-600"
          />
          <StatCard
            icon={Layers}
            value={stats.deliveryModes}
            label="Delivery Modes"
            color="from-orange-500 to-orange-600"
          />
        </div>
      </section>

      {/* Filters Section */}
      <section className="container mx-auto px-4">
        <TrainingFilters
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          industryFilter={industryFilter}
          setIndustryFilter={setIndustryFilter}
          levelFilter={levelFilter}
          setLevelFilter={setLevelFilter}
          modeFilter={modeFilter}
          setModeFilter={setModeFilter}
          totalResults={filteredTrainings.length}
        />
      </section>

      {/* Training Cards Grid */}
      <section ref={ref} className="container mx-auto px-4">
        {filteredTrainings.length > 0 ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredTrainings.map((training) => (
              <TrainingCard
                key={`${training.industry.slug}-${training.id}`}
                training={training}
                variants={cardVariants}
              />
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              No Training Programs Found
            </h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Try adjusting your search or filter criteria to find the training program you're looking for.
            </p>
          </motion.div>
        )}
      </section>
    </div>
  );
};
